import React from "react";
import Plot from "react-plotly.js";
import * as Plotly from "plotly.js";

interface TractabilityData {
  Gene_name: string;
  Shift_to_goal_end: number;
  Small_Molecule_Tractability: number;
  Antibody_Tractability: number;
  PROTAC_Tractability: number;
  Literature_Evidence: number;
  Clinical_Precedence: number;
  [key: string]: string | number; // Allow string for Gene_name and numbers for other fields
}

interface TractabilityHeatmapProps {
  data: TractabilityData[];
  topN?: number;
  sortColumn?: string;
  highlightGene?: string;
}

interface Shape {
  type: "rect";
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  line: {
    color: string;
    width: number;
  };
  fillcolor: string;
}

export const TractabilityHeatmap: React.FC<TractabilityHeatmapProps> = ({
  data,
  topN = 20,
  sortColumn = "Shift_to_goal_end",
  highlightGene,
}) => {
  // Process data similar to Python implementation
  const processData = () => {
    // Sort data by the specified column
    const sortedData = [...data].sort(
      (a, b) => (b[sortColumn] as number) - (a[sortColumn] as number)
    );
    const topData = sortedData.slice(0, topN);

    // Get tractability columns (exclude known non-tractability columns)
    const excludedColumns = [
      "Gene_name",
      "Shift_to_goal_end",
      "Goal_end_FDR",
      "N_Detections",
    ];
    const tractabilityColumns = Object.keys(data[0]).filter(
      (key) => !excludedColumns.includes(key)
    );

    // Prepare data for heatmap
    const genes = topData.map((d) => d.Gene_name);
    const zValues = topData.map((row) =>
      tractabilityColumns.map((col) => {
        const val = row[col];
        return val === "" ? null : Number(val);
      })
    );

    return {
      genes,
      features: tractabilityColumns,
      values: zValues,
    };
  };

  const { genes, features, values } = processData();

  // Find highlighted gene index
  const highlightIndex = highlightGene ? genes.indexOf(highlightGene) : -1;

  // Create Plotly data
  const heatmapData: Plotly.Data[] = [
    {
      type: "heatmap" as const,
      z: values,
      x: features,
      y: genes,
      colorscale: [
        [0, "#f0f9e8"], // Very light green for 0
        [1, "#a8ddb5"], // Green for 1
      ],
      showscale: false,
      showlegend: false,
      xgap: 1, // Add small gaps between cells
      ygap: 1,
      hovertemplate:
        "Gene: %{y}<br>" + "Feature: %{x}<br>" + "Value: %{z}<extra></extra>",
      hoverlabel: {
        bgcolor: "white",
        font: { size: 12 },
      },
    },
    // Legend entries for Present/Absent
    {
      type: "scatter" as const,
      x: [null],
      y: [null],
      mode: "markers",
      marker: {
        color: "#a8ddb5",
        size: 10,
        line: { color: "#a8ddb5", width: 1 },
      },
      name: "Present (1)",
      showlegend: true,
      hoverinfo: "none" as const,
    },
    {
      type: "scatter" as const,
      x: [null],
      y: [null],
      mode: "markers",
      marker: {
        color: "#f0f9e8",
        size: 10,
        line: { color: "#f0f9e8", width: 1 },
      },
      name: "Absent (0)",
      showlegend: true,
      hoverinfo: "none" as const,
    },
  ];

  const layout: Partial<Plotly.Layout> = {
    title: {
      text: `Tractability Features (Top ${topN} by ${sortColumn})`,
      font: { size: 14 },
    },
    autosize: true,
    margin: {
      l: 60, // Increased left margin for gene names
      r: 50, // Increased right margin for legend
      t: 50,
      b: 150, // Keep bottom margin for feature names
    },
    xaxis: {
      tickangle: 60, // Rotate feature labels
      tickfont: { size: 10 },
    },
    yaxis: {
      tickfont: { size: 10 },
    },
    legend: {
      x: 1.02,
      y: 1,
      xanchor: "left",
      yanchor: "top",
      bgcolor: "rgba(255,255,255,0.9)",
      bordercolor: "rgba(0,0,0,0.2)",
      borderwidth: 1,
      font: { size: 12 },
    },
  };

  // If there's a highlighted gene, add a rectangle annotation
  if (highlightIndex !== -1) {
    const shape: Shape = {
      type: "rect",
      x0: -0.5,
      x1: features.length - 0.5,
      y0: highlightIndex - 0.5,
      y1: highlightIndex + 0.5,
      line: {
        color: "#9333ea", // Rich purple/violet color
        width: 2,
      },
      fillcolor: "transparent",
    };
    layout.shapes = [shape];
  }

  return (
    <div className="w-full h-full">
      <Plot
        data={heatmapData}
        layout={{
          ...layout,
          autosize: true,
          margin: { l: 60, r: 50, t: 50, b: 150 },
          xaxis: {
            ...layout.xaxis,
            automargin: true,
          },
          yaxis: {
            ...layout.yaxis,
            automargin: true,
          },
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtons: [
            ["zoomIn2d", "zoomOut2d", "resetScale2d"],
            ["toImage"],
          ],
          toImageButtonOptions: {
            format: "png",
            filename: "tractability_heatmap",
          },
        }}
        className="w-full h-full"
        useResizeHandler={true}
      />
    </div>
  );
};
